// web-v2/src/lib/liability.js
//
// HOW DAVID WRITES A LIABILITY LIMIT. Prompt 100 item 1.
//
// "$1M/$100K" is a combined single limit of $1,000,000 with a $100,000
// per-passenger sublimit. The other way aviation liability is written is a
// SMOOTH limit with no per-passenger cap. They are not the same coverage and
// they do not price the same, so a surface that shows one number is showing
// half the structure.
//
// ── NOTHING WAS MISSING FROM THE RECORD ────────────────────────────────────
// The columns exist and the wizard's Coverage step already collects all three.
// Milam's two Mach 2 options carry liability_per_occurrence 1,000,000 and
// liability_per_passenger 100,000 right now. What was missing is that NO
// SURFACE RENDERED IT: the ladder row's terms line is coverage type, hull and
// effective date, and liability appears nowhere on it. That is why it read as
// "there's no way to set the liability sublimit" — the value goes in and never
// comes back out where the work is happening.
//
// ── THE THREE STATES ARE KEPT APART ────────────────────────────────────────
// `liability_smooth` is a real three-state boolean and this module refuses to
// collapse it:
//
//   true   SMOOTH. Renders "$1M CSL". A per-passenger figure alongside would be
//          a contradiction; none exists on the book (0 rows).
//   false  SPLIT. Renders "$1M/$100K".
//   null   NOBODY HAS SAID, and it must not print as either. 344 of 375 options
//          are null, of which 213 carry a per-passenger figure. Those numbers
//          are evidence of a split limit and this module RENDERS them as
//          "$1M/$100K" because that is what the two numbers say — but it does
//          NOT write the flag, and it never prints "CSL" off a null, because
//          "smooth" is a claim only the broker can make.
//
// So: the sublimit is rendered from the SUBLIMIT, and the word CSL is rendered
// only from an explicit true. A null with no sublimit prints the bare limit and
// claims no structure at all.

// The two column pairs are a migration artifact and they are DISJOINT on the
// live book, not divergent: 67 rows carry liability_csl alone, 64 carry
// per_passenger alone, exactly 1 carries both members of a pair and they agree,
// and 0 disagree. So this reads them in one fixed order in one place rather
// than every surface inventing its own `??` chain. quoteCompare.js already
// names the same pairing via its `alt:` keys; keep the two in step.
// The passenger answers a server may send (utils/liability-text passengerLine, plus could_not_read when its application read
// failed).
const LINES = ['conflict', 'excluded', 'amount', 'smooth', 'not_stated', 'could_not_read'];

export function resolveLiability(o) {
  if (!o) return { perOccurrence: null, perPassenger: null, smooth: null };
  const num = (v) => (v == null || v === '' ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
  // When the server sends its passenger answer it sends the amount it judged beside it (passenger_amount): the amount shown
  // is that one, never a field from another source beside the server's line (review 2026-11-04, L7).
  const serverJudged = LINES.includes(o.passenger_line) && Object.prototype.hasOwnProperty.call(o, 'passenger_amount');
  return {
    perOccurrence: num(o.liability_csl) ?? num(o.liability_per_occurrence),
    perPassenger: serverJudged ? num(o.passenger_amount) : (num(o.per_passenger) ?? num(o.liability_per_passenger)),
    smooth: typeof o.liability_smooth === 'boolean' ? o.liability_smooth : null,
  };
}

// $1,000,000 -> "$1M", $100,000 -> "$100K", $5,000 -> "$5K", $1,500,000 ->
// "$1.5M". David's own shorthand; anything that does not reduce cleanly keeps
// its digits rather than being rounded into a wrong number.
export function compactMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return '';
  const v = Number(n);
  if (v === 0) return '$0';
  const unit = (div, suffix) => {
    const q = v / div;
    const s = Number.isInteger(q) ? String(q) : (Math.round(q * 10) / 10).toString();
    return `$${s}${suffix}`;
  };
  if (v >= 1_000_000 && (v % 100_000 === 0)) return unit(1_000_000, 'M');
  if (v >= 1_000 && (v % 100 === 0)) return unit(1_000, 'K');
  return `$${v.toLocaleString('en-US')}`;
}

/**
 * The limit as David says it. '' when there is no limit to show.
 *
 *   split / numbers present   "$1M/$100K"
 *   smooth === true           "$1M CSL"
 *   limit only, smooth null   "$1M"       (claims no structure)
 */
// THE PASSENGER LINE, ONE ANSWER FOR EVERY V2 SURFACE (the server's utils/liability-text passengerLine, over the same
// record): a stated $0 per passenger is PASSENGERS EXCLUDED (David 2026-11-04: "Carrier sends and documents must say
// excluded"), never "$1M/$0"; beside its own CSL it is 'conflict' and no surface prints either answer. No component
// re-derives this: describeLiability, the wizard's option name, the cockpit cards, the term block and the submission
// builder all ask passengerLineOf or formatLiability.
export const PASSENGER_WORDS = Object.freeze({ excluded: 'passengers excluded', conflict: 'passenger limit to be confirmed', could_not_read: 'passenger limit could not be checked' });
export function passengerLineOf(option) {
  // The server's answer when it sends one (passenger_line: utils/deal-liability, the application-vs-record check included,
  // which a screen cannot make because it never sees the application); otherwise the same rule over the record's fields.
  if (option && LINES.includes(option.passenger_line)) return option.passenger_line;
  const { perPassenger, smooth } = resolveLiability(option);
  if (perPassenger === 0 && smooth === true) return 'conflict';
  if (perPassenger === 0) return 'excluded';
  if (smooth === true) return 'smooth';   // a smooth limit has no sublimit; a stale one above $0 is ignored, as before
  return perPassenger != null ? 'amount' : 'not_stated';
}

export function formatLiability(option) {
  const { perOccurrence, perPassenger, smooth } = resolveLiability(option);
  if (perOccurrence == null && perPassenger == null) return '';
  const occ = compactMoney(perOccurrence);
  const line = passengerLineOf(option);
  // Plain words: this string is stored as a corner's option_label and reaches the client card (quoteAxes cornerLabel).
  if (line === 'conflict' || line === 'could_not_read') return occ ? `${occ}, ${PASSENGER_WORDS[line]}` : PASSENGER_WORDS[line];
  if (smooth === true) return occ ? `${occ} CSL` : '';
  if (line === 'excluded') return occ ? `${occ}, ${PASSENGER_WORDS.excluded}` : 'Passengers excluded';
  if (perPassenger != null) return occ ? `${occ}/${compactMoney(perPassenger)}` : compactMoney(perPassenger);
  return occ;
}
